import { ChildDomainService } from './domain';
import { ChildFactory } from './entity/childFactory';

import type { ChildIRepository } from './entity/childIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';

/**
 * ChildApplicationService
 */
export class ChildApplicationService {
  private service!: ChildDomainService;

  private childFactory = new ChildFactory();

  /**
   * Injection
   * @param childRepository childRepository
   * @param logger logger
   */
  public inject(
    childRepository: ChildIRepository,
    logger: Logger,
  ) {
    this.service = new ChildDomainService(logger);
    this.service.inject(childRepository);
  }

  /**
   * 子どもリスト取得
   * @returns リスト
   */
  async getChildList() {
    const items = await this.service.getChildList();
    return items;
  }

  /**
   * createChild
   * @param input 入力
   * @returns 結果
   */
  async createChild(input: ChildJson) {
    const entity = this.childFactory.createFromJsonPvt(input);
    const result = await this.service.createChild(entity);
    return result;
  }

  /**
   * updateChild
   * @param input 入力
   * @returns 結果
   */
  async updateChild(input: ChildJson) {
    const entity = this.childFactory.createFromJsonPvt(input);
    const result = await this.service.updateChild(entity);
    return result;
  }

  /**
   * deleteChild
   * @param input 入力
   * @returns 結果
   */
  async deleteChild(input: ChildJson) {
    const entity = this.childFactory.createFromJsonPvt(input);
    const result = await this.service.deleteChild(entity);
    return result;
  }

  /**
   * 子どもリストを更新する(Create)
   * @param childList 子どもリスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createChildStore(
    childList: ChildJson[],
    payload: EntitySubscribeType,
    filter: ((json: ChildJson) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePvt(
      childList,
      payload,
      this.childFactory,
      filter,
    );
  }

  /**
   * 子どもリストを更新する(Update)
   * @param childList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateChildStore(
    childList: ChildJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePvt(
      childList,
      payload,
      this.childFactory,
    );
  }

  /**
   * 子どもリストを更新する(Delete)
   * @param childList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteChildStore(
    childList: ChildJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePvt(
      childList,
      payload,
      this.childFactory,
    );
  }
}
